// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Form, Button } from "react-bootstrap";
import InputNumber from "rc-input-number";
import { IUserChargingControl, ChargingMode } from "shared/types/cloudtypes";
import { DataState } from "types";
import {
  ActionType,
  IAction,
  IActionChargingPower,
  IActionTypeChangeUser,
  raiseAction,
} from "actions";
import { isSet, round } from "shared/utils/utils";
import { IState } from "states/states";
import { IOcppChargingControl } from "states/ocppstates";

import DateInput from "./DateInput";
import SaveFeedback from "./SaveFeedback";
import cssContent from "constants.module.scss";
import { DateTime, Interval } from "luxon";

const DEFAULT_CHARGING_TIME = 4 * 3600; // s
const GET_POWER_REQ_TIMEOUT = 10 * 1000; // ms, to show loading spinner

interface IChargingPowerControlExtProps {
  isUserAdmin?: boolean; // modify user default control or active session?
  transactionId?: number; // if transactionId is undefined, we show/modify the active session
}

interface IChargingPowerControlProps {
  isRfidUser: boolean;
  isLoading: boolean;
  saveState: DataState;
  // sessionId for ongoing sessions; typically equal to transactionId but transactionId is not set for active session link
  sessionId: number | undefined;
  chargerId: number | undefined;
  userId: number | undefined;
  chargingControl: IOcppChargingControl | undefined;
  userDefaultControl: IUserChargingControl | undefined;
  sessionEnergy: number | undefined;
  carBattery: number | undefined; // size of car battery, if available
}

const mapStateToProps = (state: IState, ownProps: IChargingPowerControlExtProps) => {
  const curUser = state.site.userList?.find(
    (curUserItem) => curUserItem.secId === state.site.user?.secId,
  );
  const sessionId = ownProps.transactionId ?? state.router.transaction;
  // pendingChargingSession should be enough? chargingSession might not even contain current session
  const session =
    state.ocpp.pendingChargingSession.find((curitem) => curitem.id === sessionId) ??
    state.ocpp.chargingSession.find((curItem) => curItem.id === sessionId);
  const chargerIndex = state.ocpp.charger.findIndex(
    (curItem) => curItem.fk_charger === session?.fk_charger,
  );
  const rfid = state.ocpp.rfid.find((curItem) => curItem.rfid === state.site.user?.secId);

  const props: IChargingPowerControlProps = {
    isRfidUser: !!state.site.user?.isRfidUser,
    isLoading:
      isSet(state.scheduler.chargingPower.lastRequest) &&
      (!state.scheduler.chargingPower.lastData ||
        state.scheduler.chargingPower.lastRequest > state.scheduler.chargingPower.lastData) &&
      Date.now() - state.scheduler.chargingPower.lastRequest < GET_POWER_REQ_TIMEOUT,
    saveState: state.scheduler.saveState,
    sessionId,
    chargerId: session?.fk_charger,
    chargingControl: state.ocpp.chargingControl,
    userId: curUser?.id,
    userDefaultControl: curUser?.chargingControl,
    sessionEnergy:
      isSet(session?.connector) ?
        state.ocpp.connectorData[chargerIndex]?.[session!.connector]?.sessionEnergy
      : undefined,
    carBattery: rfid?.carBattery,
  };
  return props;
};

interface IChargingPowerControlState {
  lastId: number | undefined;
  isChanged: boolean;
  isActive: boolean;
  saveAsDefault: boolean;
  useAtNewSession: boolean;
  mode: ChargingMode;
  maxPower: number | undefined;
  targetEnergy: number | undefined;
  targetTimestampMs: number;
}

interface IChargingPowerControlDispatch {
  onSaveSession: (
    state: IChargingPowerControlState,
    isActive: boolean,
    sessionId: number,
    chargerId: number,
  ) => void;
  onSaveUser: (state: IChargingPowerControlState, isActive: boolean, userId: number) => void;
}

const mapDispatchToProps = (
  dispatch: (action: IAction<any>) => void,
  ownProps: IChargingPowerControlExtProps,
) => {
  const dispatchProps: IChargingPowerControlDispatch = {
    onSaveSession: (
      state: IChargingPowerControlState,
      isActive: boolean,
      sessionId: number,
      chargerId: number,
    ) => {
      const { isChanged, lastId, targetTimestampMs, ...data } = state;
      const payload: IActionChargingPower = {
        chargerId,
        transactionId: sessionId,
        targetTimestamp: Math.round(targetTimestampMs / 1000),
        ...data,
        isActive,
      };
      dispatch(raiseAction(ActionType.ACT_SAVE_CHARGER_POWER_REQ, payload));
    },
    onSaveUser: (state: IChargingPowerControlState, isActive: boolean, userId: number) => {
      const { isChanged, lastId, targetTimestampMs, ...data } = state;
      const payload: IActionTypeChangeUser = {
        id: userId,
        chargerControl: {
          targetTimestamp: Math.round(targetTimestampMs / 1000),
          ...data,
          isActive,
        },
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_USER_REQ, payload));
    },
  };

  return dispatchProps;
};

const messages = defineMessages({});

class ChargingPowerControlComponent extends React.Component<
  IChargingPowerControlExtProps & IChargingPowerControlProps & IChargingPowerControlDispatch,
  IChargingPowerControlState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public state: IChargingPowerControlState = {
    lastId: -1, // ensure at least one update
    isChanged: false,
    isActive: false,
    saveAsDefault: false,
    useAtNewSession: true,
    mode: ChargingMode.Early,
    maxPower: undefined,
    targetEnergy: undefined,
    targetTimestampMs: Date.now() + DEFAULT_CHARGING_TIME * 1000,
  };

  public componentDidMount() {
    this.updateState();
  }
  public componentDidUpdate() {
    this.updateState();
  }

  protected updateState() {
    if (this.props.chargingControl?.id !== this.state.lastId) {
      const currentControl =
        this.props.isUserAdmin ? this.props.userDefaultControl : this.props.chargingControl;
      // adjust target timestamp to next day
      let targetTimestamp = currentControl?.targetTimestamp ?? undefined;
      if (this.props.chargingControl === undefined && isSet(targetTimestamp)) {
        // see OcppBackend::StartTransaction::setupDefaultControl
        const oldTarget = DateTime.fromSeconds(targetTimestamp);
        const targetTime = Interval.fromDateTimes(oldTarget.startOf("day"), oldTarget).toDuration();
        const now = DateTime.now();
        const curTime = Interval.fromDateTimes(now.startOf("day"), now).toDuration();
        targetTimestamp =
          targetTime > curTime ?
            now.startOf("day").plus(targetTime).toSeconds()
          : now.endOf("day").plus(targetTime).toSeconds();
      }
      this.setState({
        lastId: this.props.chargingControl?.id,
        // allow saving of user default data
        isChanged: Boolean(this.props.isUserAdmin) && isSet(this.props.userDefaultControl),
        isActive: currentControl?.isActive ?? true,
        mode: currentControl?.mode ?? ChargingMode.Early,
        maxPower: currentControl?.maxPower ?? undefined,
        targetEnergy: currentControl?.targetEnergy ?? undefined,
        targetTimestampMs:
          targetTimestamp ? targetTimestamp * 1000 : Date.now() + DEFAULT_CHARGING_TIME * 1000,
      });
    }
  }

  public render() {
    return (
      <div id="chargingpowercontrol">
        <h2>
          {!this.props.isUserAdmin && (
            <>
              <Form.Label htmlFor="isActive" className="me-2 link">
                <FormattedMessage id="Charging Control" />
              </Form.Label>
              {this.props.isLoading ?
                <div id="loading-spinner"></div>
              : <Form.Check
                  id="isActive"
                  style={{ marginTop: 1 }}
                  checked={this.state.isActive}
                  disabled={this.props.chargingControl?.isActive}
                  onChange={(event) => {
                    this.setState({
                      isChanged: true,
                      isActive: event.currentTarget.checked,
                    });
                  }}
                />
              }
            </>
          )}
        </h2>
        {(this.state.isActive || Boolean(this.props.isUserAdmin)) && (
          <>
            {this.renderSettings()}
            <Button
              variant="secondary"
              className="me-2"
              disabled={!this.state.isChanged || this.props.saveState === DataState.SAVE_REQ}
              onClick={() => {
                const isActive = isSet(this.state.maxPower) || isSet(this.state.targetEnergy);
                this.setState({
                  isChanged: false,
                  isActive,
                });
                // we need to pass isActive explicitely, as the state update is not done yet
                if (this.props.isUserAdmin) {
                  this.props.onSaveUser(this.state, isActive, this.props.userId!);
                } else {
                  this.props.onSaveSession(
                    this.state,
                    isActive,
                    this.props.sessionId!,
                    this.props.chargerId!,
                  );
                }
              }}
            >
              <FormattedMessage id="Save" />
            </Button>
          </>
        )}
        <SaveFeedback className="mt-2" dataState={this.props.saveState} />
      </div>
    );
  }

  private renderSettings() {
    const modeItems = [
      ChargingMode.Early,
      ChargingMode.Late,
      ChargingMode.Uniform,
      ChargingMode.CostOptimized,
      ChargingMode.PVOptimized,
    ].map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.context.formatMessage({ id: `ChargingMode_${curItem}` })}
        </option>
      );
    });

    return (
      <>
        <table className="mb-2">
          <tbody>
            <tr className="mt-3 no-white">
              <th colSpan={2}>
                <Form.Check
                  id="maxPowerChk"
                  className="me-2"
                  style={{ marginTop: 3 }}
                  checked={isSet(this.state.maxPower)}
                  onChange={(event) => {
                    this.setState({
                      isChanged: true,
                      maxPower: event.currentTarget.checked ? 11 : undefined,
                    });
                  }}
                  label={<FormattedMessage id="Limit power" />}
                />
              </th>
            </tr>
            {isSet(this.state.maxPower) && (
              <>
                <tr className="mt-2">
                  <td>
                    <FormattedMessage id="Limit the power to" />
                  </td>
                  <td>
                    <InputNumber
                      id="maxPower"
                      type="number"
                      min={0}
                      value={this.state.maxPower}
                      onChange={(number) => {
                        if (isSet(number)) {
                          this.setState({
                            isChanged: true,
                            maxPower: number,
                          });
                        }
                      }}
                    />
                    <span> kW</span>
                  </td>
                </tr>
                {this.state.maxPower < 4 && (
                  <tr>
                    <td
                      colSpan={2}
                      style={{ color: cssContent.CHARGER_COLOR_AVAILABLE, paddingTop: 0 }}
                    >
                      <FormattedMessage id="Please notice that a minimum charging power exists that cannot be fallen below. The resulting charging power might be increased." />
                    </td>
                  </tr>
                )}
              </>
            )}
            <tr className="mt-3 no-white">
              <th colSpan={2}>
                <Form.Check
                  id="targetEnergyChk"
                  className="me-2"
                  style={{ marginTop: 3 }}
                  checked={isSet(this.state.targetEnergy)}
                  onChange={(event) => {
                    this.setState({
                      isChanged: true,
                      targetEnergy: event.currentTarget.checked ? 40 : undefined,
                    });
                  }}
                  label={<FormattedMessage id="Limit energy" />}
                />
              </th>
            </tr>
            {isSet(this.state.targetEnergy) && (
              <>
                <tr className="mt-2">
                  <td>
                    <Form.Label htmlFor="targetEnergy">
                      <FormattedMessage id="Limit the energy to" />
                    </Form.Label>
                  </td>
                  <td>
                    {isSet(this.props.carBattery) ?
                      <>
                        <InputNumber
                          id="targetEnergy"
                          type="number"
                          min={0}
                          value={Math.round(
                            (100 * this.state.targetEnergy) / this.props.carBattery,
                          )}
                          onChange={(value: number | null) => {
                            if (isSet(value)) {
                              this.setState({
                                isChanged: true,
                                targetEnergy: round((value * this.props.carBattery!) / 100, 1),
                              });
                            }
                          }}
                        />
                        % <span className="ms-1">(={this.state.targetEnergy} kWh)</span>
                      </>
                    : <>
                        <InputNumber
                          id="targetEnergy"
                          type="number"
                          min={0}
                          value={this.state.targetEnergy}
                          onChange={(value: number | null) => {
                            if (isSet(value)) {
                              this.setState({
                                isChanged: true,
                                targetEnergy: value,
                              });
                            }
                          }}
                        />{" "}
                        kWh
                      </>
                    }
                  </td>
                </tr>
                {isSet(this.props.sessionEnergy) && (
                  <tr>
                    <td
                      colSpan={2}
                      style={{ color: cssContent.CHARGER_COLOR_AVAILABLE, paddingTop: 0 }}
                    >
                      <FormattedMessage id="Already charged:" />{" "}
                      {round(this.props.sessionEnergy, 1)} kWh
                    </td>
                  </tr>
                )}
                <tr className="mt-2">
                  <td>
                    <Form.Label htmlFor="mode">
                      <FormattedMessage id="Charging Strategy" />
                    </Form.Label>
                  </td>
                  <td>
                    <Form.Select
                      id="mode"
                      style={{ marginTop: -5 }}
                      value={this.state.mode}
                      onChange={(event) => {
                        const mode = event.currentTarget.value as ChargingMode;
                        this.setState({
                          isChanged: true,
                          mode,
                        });
                      }}
                    >
                      {modeItems}
                    </Form.Select>
                  </td>
                </tr>
                <tr>
                  <td
                    colSpan={2}
                    style={{ color: cssContent.CHARGER_COLOR_AVAILABLE, paddingTop: 0 }}
                  >
                    {this.state.mode === ChargingMode.Early && (
                      <FormattedMessage id="Charge immediately and stop after requested energy is reached." />
                    )}
                    {this.state.mode === ChargingMode.Late && (
                      <FormattedMessage id="Charge as late as possible to match requested energy and departure time." />
                    )}
                    {this.state.mode === ChargingMode.Uniform && (
                      <FormattedMessage id="Charge with least possible power to match requested energy and departure time. Please notice that a minimum charging power exists that cannot be fallen below." />
                    )}
                    {this.state.mode === ChargingMode.CostOptimized && (
                      <FormattedMessage id="Charge in those periods where the energy prices (EPEX Spot) are lowest. This is cost optimal if you have a variable energy price and environmental friendly as it uses off-peak periods." />
                    )}
                    {this.state.mode === ChargingMode.CostOptimized && (
                      <FormattedMessage id="Charge in those periods where Photo Voltaic power is maximum during the period until the departure time." />
                    )}
                  </td>
                </tr>
                {this.state.mode !== ChargingMode.Early &&
                  !this.props.isUserAdmin &&
                  this.renderTargetTimestamp()}
              </>
            )}
            {this.props.isRfidUser && Boolean(this.props.isUserAdmin) && (
              <tr className="mt-3 no-white">
                <td colSpan={2}>
                  <Form.Check
                    id="useAtNewSession"
                    className="me-2"
                    style={{ marginTop: 1 }}
                    checked={this.state.useAtNewSession}
                    onChange={(event) => {
                      this.setState({
                        isChanged: true,
                        useAtNewSession: event.currentTarget.checked,
                      });
                    }}
                    label={
                      <FormattedMessage id="Automatically activate this charging strategy for new charging sessions" />
                    }
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {isSet(this.state.maxPower) &&
          isSet(this.state.targetEnergy) &&
          this.state.mode === ChargingMode.Uniform && (
            <p>
              <FormattedMessage id="Please notice that if you limit both, the power and the energy, the resulting lower charging limit will be used." />
            </p>
          )}
      </>
    );
  }

  private renderTargetTimestamp() {
    return (
      <tr className="mt-2">
        <td>
          <Form.Label htmlFor="targetTimestamp">
            <FormattedMessage id="Departure time" />
          </Form.Label>
        </td>
        <td>
          <DateInput
            type="datetime-local"
            id="targetTimestamp"
            style={{ width: 230, marginTop: -5 }}
            className="me-1"
            valueMs={this.state.targetTimestampMs}
            onChange={(targetTimestampMs) => {
              this.setState({ isChanged: true, targetTimestampMs });
            }}
          />
        </td>
      </tr>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ChargingPowerControlComponent);
